import React, { useState } from "react";
import { LayoutDashboard, Package, ShoppingCart, RotateCcw, ClipboardCheck, Building2, ShieldCheck, ChevronDown, FileDown } from "lucide-react";
import { canManageParts, canCreatePartsRequest, getPartsRole, PARTS_TIER_LABELS } from "@/utils/permissions";
import { useT } from "@/i18n/LanguageContext";
import { InventoryDashboard } from "./InventoryDashboard";
import { PartsInventory } from "./PartsInventory";
import { RequestsSection } from "./RequestsSection";
import { PurchaseOrdersSection } from "./PurchaseOrdersSection";
import { ReceivingSection } from "./ReceivingSection";
import { CoreTrackingSection } from "./CoreTrackingSection";
import { SalvagedPartsSection } from "./SalvagedPartsSection";
import { TireInventorySection } from "./TireInventorySection";
import { AdjustmentsSection } from "./AdjustmentsSection";
import { CycleCountSection } from "./CycleCountSection";
import { AuditSection } from "./AuditSection";
import { VendorsSection } from "./VendorsSection";
import { LocationsSection } from "./LocationsSection";
import { TransactionLedger } from "./TransactionLedger";

const GROUPS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, items: [{ id: "dashboard", label: "Dashboard" }] },
  { id: "inventory", label: "Inventory", icon: Package, items: [
    { id: "inventory", label: "Parts" }, { id: "salvaged", label: "Salvaged" },
    { id: "tires", label: "Tires" }, { id: "locations", label: "Locations" },
  ] },
  { id: "procurement", label: "Procurement", icon: ShoppingCart, items: [
    { id: "requests", label: "Requests" }, { id: "purchase-orders", label: "Purchase Orders" }, { id: "receiving", label: "Receive" },
  ] },
  { id: "cores", label: "Core Tracking", icon: RotateCcw, items: [{ id: "cores", label: "Core Tracking" }] },
  { id: "counts", label: "Counts & Ledger", icon: ClipboardCheck, items: [
    { id: "adjustments", label: "Adjustments" }, { id: "cycle-counts", label: "Cycle Counts" },
    { id: "ledger", label: "Ledger" }, { id: "audit", label: "Audit Trail" },
  ] },
  { id: "vendors", label: "Vendors", icon: Building2, items: [{ id: "vendors", label: "Vendors" }] },
];

export const InventoryModule = ({ currentUser, vehicles = [] }) => {
  const [leaf, setLeaf] = useState("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const { t, lang } = useT();
  const canManage = canManageParts(currentUser);
  const canCreate = canCreatePartsRequest(currentUser);
  const partsRole = getPartsRole(currentUser);
  const activeGroup = GROUPS.find((g) => g.items.some((i) => i.id === leaf)) || GROUPS[0];
  const activeItem = activeGroup.items.find((i) => i.id === leaf) || activeGroup.items[0];
  const ActiveIcon = activeGroup.icon;

  const selectLeaf = (id) => { setLeaf(id); setMenuOpen(false); };

  const render = () => {
    switch (leaf) {
      case "dashboard": return <InventoryDashboard />;
      case "inventory": return <PartsInventory currentUser={currentUser} canManage={canManage} vehicles={vehicles} />;
      case "salvaged": return <SalvagedPartsSection currentUser={currentUser} canManage={canManage} vehicles={vehicles} />;
      case "tires": return <TireInventorySection currentUser={currentUser} canManage={canManage} vehicles={vehicles} />;
      case "locations": return <LocationsSection currentUser={currentUser} canManage={canManage} />;
      case "requests": return <RequestsSection currentUser={currentUser} canManage={canManage} canCreate={canCreate} vehicles={vehicles} />;
      case "purchase-orders": return <PurchaseOrdersSection currentUser={currentUser} canManage={canManage} vehicles={vehicles} />;
      case "receiving": return <ReceivingSection currentUser={currentUser} canManage={canManage} vehicles={vehicles} />;
      case "cores": return <CoreTrackingSection currentUser={currentUser} canManage={canManage} vehicles={vehicles} />;
      case "adjustments": return <AdjustmentsSection currentUser={currentUser} canManage={canManage} />;
      case "cycle-counts": return <CycleCountSection currentUser={currentUser} canManage={canManage} />;
      case "ledger": return <TransactionLedger />;
      case "audit": return <AuditSection />;
      case "vendors": return <VendorsSection currentUser={currentUser} canManage={canManage} />;
      default: return null;
    }
  };

  const roleBadgeColor = { admin: "bg-blue-100 text-blue-700", parts_manager: "bg-green-100 text-green-700", mechanic: "bg-amber-100 text-amber-700", viewer: "bg-slate-100 text-slate-600" }[partsRole];

  return (
    <div className="p-4 sm:p-6 lg:p-8 pt-16 sm:pt-6 lg:pt-8 max-w-6xl mx-auto" data-testid="inventory-module">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{t("Parts & Inventory")}</h1>
          <p className="hidden sm:block text-sm text-slate-500 mt-0.5">{t("Track parts, costs, and chain of custody across the fleet")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={lang === "es" ? "/inventory-user-guide-es.pdf" : "/inventory-user-guide.pdf"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="user-guide-link"
            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
          >
            <FileDown className="w-3.5 h-3.5" /> {t("Guide")}
          </a>
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${roleBadgeColor}`} data-testid="parts-role-badge">
            <ShieldCheck className="w-3.5 h-3.5" /> {t(PARTS_TIER_LABELS[partsRole])}
          </span>
        </div>
      </div>

      {/* Mobile: tap-to-open grouped dropdown menu (no horizontal scrolling) */}
      <div className="lg:hidden relative mb-3" data-testid="inv-mobile-nav">
        <button
          onClick={() => setMenuOpen((o) => !o)}
          data-testid="inv-mobile-nav-trigger"
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-blue-600 text-white font-medium"
        >
          <span className="flex items-center gap-2 min-w-0">
            <ActiveIcon className="w-4 h-4 shrink-0" />
            <span className="truncate">{activeGroup.items.length > 1 ? `${t(activeGroup.label)} · ${t(activeItem.label)}` : t(activeGroup.label)}</span>
          </span>
          <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
            <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-[65vh] overflow-y-auto py-1" data-testid="inv-mobile-menu">
              {GROUPS.map((g) => {
                const Icon = g.icon;
                if (g.items.length === 1) {
                  const i = g.items[0];
                  return (
                    <button
                      key={g.id}
                      onClick={() => selectLeaf(i.id)}
                      data-testid={`inv-mobile-${i.id}`}
                      className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm text-left ${leaf === i.id ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}
                    >
                      <Icon className="w-4 h-4 shrink-0" /> {t(g.label)}
                    </button>
                  );
                }
                return (
                  <div key={g.id} className="border-t border-slate-100 first:border-t-0">
                    <div className="px-4 pt-2.5 pb-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                      <Icon className="w-3.5 h-3.5" /> {t(g.label)}
                    </div>
                    {g.items.map((i) => (
                      <button
                        key={i.id}
                        onClick={() => selectLeaf(i.id)}
                        data-testid={`inv-mobile-${i.id}`}
                        className={`w-full text-left pl-11 pr-4 py-2.5 text-sm ${leaf === i.id ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}
                      >
                        {t(i.label)}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Desktop: grouped pill navigation */}
      <div className="hidden lg:block">
        <div className="flex gap-1 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
          {GROUPS.map((g) => {
            const Icon = g.icon;
            const active = activeGroup.id === g.id;
            const single = g.items.length === 1;
            return (
              <button
                key={g.id}
                onClick={() => setLeaf(g.items[0].id)}
                data-testid={single ? `inv-tab-${g.items[0].id}` : `inv-group-${g.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${
                  active ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                }`}
              >
                <Icon className="w-4 h-4" /> {t(g.label)}
              </button>
            );
          })}
        </div>

        {/* Sub-tabs for active group (only when >1 item) */}
        {activeGroup.items.length > 1 && (
          <div className="flex gap-1 overflow-x-auto py-2 mb-3 -mx-1 px-1 scrollbar-hide border-b border-slate-100">
            {activeGroup.items.map((i) => (
              <button
                key={i.id}
                onClick={() => setLeaf(i.id)}
                data-testid={`inv-tab-${i.id}`}
                className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${
                  leaf === i.id ? "bg-blue-50 text-blue-700 border border-blue-200" : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                {t(i.label)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3">{render()}</div>
    </div>
  );
};
